// «سعود» — what the assistant may know about the page the school is looking at.
//
// buildSnapshot() reads the current page (route, title, filters, stat cards, headings/alerts, tables) and returns
//   { snapshot, json, tables }
//   snapshot / json — the compact PageSnapshot sent to the server (≤ 8 KB, NO personal values: names, phones,
//                     IDs, e-mails never leave the browser — their columns are sent as labels only)
//   tables          — the full rows of every table (personal values included), kept in the browser only, so the
//                     actions (Excel / PDF / image / chart) can be built locally from the page's own data.
//
// Tables come from vue-good-table and b-table instances (rows from the component data = every filtered row,
// not only the visible page), else from plain <table> elements in the DOM (the app renders all rows with v-for).
// Every table keeps `rows` (what the page shows now) and `allRows` (everything the page holds); a page whose
// server sends one page at a time has an adapter (adapters/) that can fetch «كل البيانات».

import { adapterFor } from './adapters';

var MAX_BYTES = 8000;          // the contract says ≤ 8 KB; keep a little margin for the server's own trimming
var MAX_TABLES = 6;
var MAX_COLS = 24;
var MAX_ROWS = 20000;

// personal by label (Arabic + English) — the contract list
var PERSONAL_LABEL = /(اسم|الاسم|إسم|جوال|الجوال|هاتف|الهاتف|موبايل|واتساب|هوية|الهوية|سجل|مدني|إقامة|اقامة|بريد|ايميل|إيميل|رمز|كلمة المرور|كلمة السر|كلمه المرور|السري)|\b(name|names|phone|phones|mobile|tel|whatsapp|id|ids|national|iqama|email|e-mail|mail|password|passcode|pin)\b/i;
// a person written in the column (names) — personal when the values are free text (not an enum/number)
var PERSON_LABEL = /(طالب|طلاب|الطالبة|طالبات|معلم|المعلمة|معلمين|معلمات|ولي الأمر|ولي الامر|ولى الامر|الموظف|موظف|المشرف|مشرف|المستخدم|المرسل|المستلم|بواسطة|المسجل|المسجّل|المدرس|الوكيل|الموجه|ابن|ابنة)|\b(student|teacher|parent|user|employee|staff|sender|recipient)\b/i;
// a column labelled just «الطالب / المعلم / الموظف …» holds names: personal whatever its values look like
// (the server applies the same rule)
var PERSON_EXACT = /^(ال)?(طالب|طلاب|طالبة|طالبات|معلم|معلمة|معلمين|معلمات|موظف|موظفة|موظفين|مشرف|مستخدم|ولي الأمر|ولي الامر)$/;
// columns that are only buttons / row numbers
var SKIP_LABEL = /^(خيارات|الخيارات|اجراءات|إجراءات|الإجراءات|تحكم|التحكم|عمليات|options|actions|action|#|م|مسلسل|ت|حذف|تعديل)$/i;
var SKIP_CLASS = /(^|\s)(none|dnone|d-none|hidemonprint|no-print|noprint|d-print-none|sr-only)(\s|$)/;

var PHONE_RE = /(^|[^\d])(\+?966\s?5\d{8}|05\d{8}|5\d{8})([^\d]|$)/;
var NATID_RE = /(^|[^\d])[12]\d{9}([^\d]|$)/;
var EMAIL_RE = /[^\s@]+@[^\s@]+\.[a-z]{2,}/i;

var AR_DIGITS = /[٠-٩۰-۹]/g;
function latinDigits(s){
  return String(s).replace(AR_DIGITS, function(d){
    var c = d.charCodeAt(0);
    return String(c >= 0x06F0 ? c - 0x06F0 : c - 0x0660);
  });
}

function clean(s, max){
  s = String(s == null ? '' : s).replace(/[\u200e\u200f\u202a-\u202e]/g, '').replace(/[ \t\u00a0]+/g, ' ')
    .replace(/\s*\n\s*/g, '\n').trim();
  if (max && s.length > max) s = s.slice(0, max - 1) + '…';
  return s;
}

// numbers that look like phones / IDs (8+ digits) and e-mails are masked in every free text that leaves the
// browser (the server also strips 9+ digit numbers and e-mails)
export function maskPrivate(s){
  return String(s || '').replace(/[^\s@]+@[^\s@]+\.[a-z]{2,}/gi, '•••@•••')
    .replace(/[+\d٠-٩][\d٠-٩ \-]{6,}[\d٠-٩]/g, function(m){
      var t = latinDigits(m).trim();
      if (/^\d{4}-\d{1,2}-\d{1,2}$|^\d{1,2}-\d{1,2}-\d{4}$/.test(t)) return m;          // a date, not a number
      return t.replace(/\D/g, '').length >= 8 ? m.replace(/[\d٠-٩]/g, '•') : m;
    });
}

export function byteLength(s){
  try { return new TextEncoder().encode(s).length; } catch (e) { return unescape(encodeURIComponent(s)).length; }
}

function isVisible(el){
  if (!el || !el.getClientRects || !el.getClientRects().length) return false;
  var st = window.getComputedStyle(el);
  return st.visibility != 'hidden' && st.display != 'none';
}

// the part of the app the school is looking at: page content (+ an open modal), never the menu or سعود itself
function roots(){
  var list = [];
  var main = document.querySelector('.content-body') || document.querySelector('.app-content') || document.getElementById('app');
  if (main) list.push(main);
  document.querySelectorAll('.modal.show .modal-content').forEach(function(m){ if (!inSaud(m)) list.push(m); });
  return list;
}

function inSaud(el){
  return !!(el && el.closest && el.closest('.saud-root, .saud-chart-modal'));
}

function skipEl(el){
  if (el.nodeType != 1) return false;
  var t = el.tagName;
  if (t == 'BUTTON' || t == 'SCRIPT' || t == 'STYLE' || t == 'svg' || t == 'SVG' || t == 'IMG' || t == 'NOSCRIPT' || t == 'TEMPLATE') return true;
  if (el.classList && el.classList.contains('dropdown-menu')) return true;
  if (typeof el.className == 'string' && SKIP_CLASS.test(el.className)) return true;
  if (el.style && el.style.display == 'none') return true;
  if (el.hidden) return true;
  return false;
}

var BLOCK = { DIV: 1, P: 1, LI: 1, UL: 1, OL: 1, TR: 1, H1: 1, H2: 1, H3: 1, H4: 1, H5: 1, H6: 1, TABLE: 1 };

// text of a cell as the school reads it: inputs by value, selects by the chosen option, buttons skipped
export function cellText(cell){
  var out = [];
  (function walk(n){
    for (var c = n.firstChild; c; c = c.nextSibling){
      if (c.nodeType == 3){ out.push(c.nodeValue); continue; }
      if (c.nodeType != 1 || skipEl(c)) continue;
      var t = c.tagName;
      if (t == 'BR'){ out.push('\n'); continue; }
      if (t == 'INPUT'){
        var ty = (c.type || '').toLowerCase();
        if (ty == 'checkbox' || ty == 'radio'){ if (c.checked && !c.closest('label')) out.push('✓'); }
        else if (ty != 'hidden' && ty != 'password' && ty != 'file' && ty != 'button' && ty != 'submit') out.push(c.value);
        continue;
      }
      if (t == 'SELECT'){ var o = c.options[c.selectedIndex]; if (o) out.push(o.text); continue; }
      if (t == 'TEXTAREA'){ out.push(c.value); continue; }
      if (BLOCK[t]) out.push('\n');
      walk(c);
      if (BLOCK[t]) out.push('\n');
    }
  })(cell);
  return clean(out.join(''), 400);
}

/* ---------------------------------------------------------------- tables ---------------------------------------- */

// every vue-good-table / b-table instance on the page, through the Vue component tree
function componentTables(){
  var app = document.getElementById('app');
  var root = app && app.__vue__;
  var found = [];
  if (!root) return found;
  var stack = [root.$root || root], seen = 0;
  while (stack.length && seen < 8000){
    var vm = stack.pop(); seen++;
    var name = vm.$options && (vm.$options.name || vm.$options._componentTag);
    if ((name == 'vue-good-table' || name == 'VueGoodTable' || name == 'BTable') && vm.$el && vm.$el.nodeType == 1 && isVisible(vm.$el) && !inSaud(vm.$el)){
      found.push({ vm: vm, kind: name == 'BTable' ? 'btable' : 'vgt', el: vm.$el });
      continue;
    }
    (vm.$children || []).forEach(function(c){ stack.push(c); });
  }
  return found;
}

function plainValue(v){
  if (v == null) return '';
  if (Array.isArray(v)) return v.map(plainValue).join('، ');
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v == 'object') return '';
  return clean(String(v).replace(/<[^>]*>/g, ' '), 400);
}

function fromGoodTable(vm){
  var cols = (vm.typedColumns || vm.columns || []).filter(function(c){ return c && !c.hidden; });
  var cellsOf = function(r){
    return cols.map(function(c){
      try { return plainValue(vm.collectFormatted ? vm.collectFormatted(r, c) : r[c.field]); } catch (e) { return ''; }
    });
  };
  var flat = function(groups){
    var out = [];
    (groups || []).forEach(function(h){
      (h && h.children ? h.children : [h]).forEach(function(r){ if (out.length < MAX_ROWS) out.push(cellsOf(r)); });
    });
    return out;
  };
  var all = flat(vm.processedRows || vm.filteredRows);
  if (!all.length && Array.isArray(vm.rows)) all = vm.rows.slice(0, MAX_ROWS).map(cellsOf);
  // the page of a client-paginated table (vgt `paginated`); without pagination it is every row
  var page = vm.paginated ? flat(vm.paginated) : all;
  return { labels: cols.map(function(c){ return clean(c.label || c.field || '', 60); }), rows: page, allRows: all };
}

function fromBTable(vm){
  var fields = (vm.computedFields || []).filter(function(f){ return f && f.key; });
  var cellsOf = function(it){
    return fields.map(function(f){
      try { return plainValue(vm.getFormattedValue ? vm.getFormattedValue(it, f) : it[f.key]); } catch (e) { return ''; }
    });
  };
  var all = (vm.sortedItems || vm.filteredItems || vm.computedItems || vm.localItems || []).slice(0, MAX_ROWS).map(cellsOf);
  var page = vm.computedItems ? vm.computedItems.slice(0, MAX_ROWS).map(cellsOf) : all;
  return { labels: fields.map(function(f){ return clean(f.label || f.key, 60); }), rows: page, allRows: all };
}

// header labels of a plain table: thead rows (colspan / rowspan expanded) joined top-down
function headerLabels(table){
  var hrows = [];
  if (table.tHead && table.tHead.rows.length) hrows = Array.prototype.slice.call(table.tHead.rows);
  else {
    // no <thead>: the first row when it is made of <th>
    var first = table.rows[0];
    if (first && first.cells.length && Array.prototype.every.call(first.cells, function(c){ return c.tagName == 'TH'; })) hrows = [first];
  }
  // some pages put <th> directly in <thead> without a <tr>
  if (!hrows.length && table.tHead){
    var ths = table.tHead.querySelectorAll('th');
    if (ths.length) return { labels: Array.prototype.map.call(ths, function(th){ return { text: cellText(th), skip: skipHeader(th) }; }), rows: [] };
  }
  var grid = [];
  hrows.forEach(function(tr, ri){
    grid[ri] = grid[ri] || [];
    var ci = 0;
    Array.prototype.forEach.call(tr.cells, function(cell){
      while (grid[ri][ci]) ci++;
      var cs = Math.max(1, cell.colSpan || 1), rs = Math.max(1, cell.rowSpan || 1);
      var item = { text: cellText(cell), skip: skipHeader(cell) };
      for (var r = 0; r < rs; r++){
        grid[ri + r] = grid[ri + r] || [];
        for (var c = 0; c < cs; c++) grid[ri + r][ci + c] = item;
      }
      ci += cs;
    });
  });
  var width = grid.reduce(function(m, r){ return Math.max(m, r.length); }, 0);
  var labels = [];
  for (var c = 0; c < width; c++){
    var parts = [], skip = false;
    grid.forEach(function(r){
      var it = r[c];
      if (!it) return;
      if (it.skip) skip = true;
      if (it.text && parts[parts.length - 1] != it.text) parts.push(it.text);
    });
    labels.push({ text: parts.join(' - '), skip: skip });
  }
  return { labels: labels, rows: hrows };
}

function skipHeader(th){
  if (typeof th.className == 'string' && SKIP_CLASS.test(th.className)) return true;
  if (th.style && th.style.display == 'none') return true;
  return false;
}

function fromPlainTable(table){
  var head = headerLabels(table);
  var headRows = head.rows;
  var labels = head.labels;
  var body = [];
  var trs = table.tBodies.length
    ? Array.prototype.concat.apply([], Array.prototype.map.call(table.tBodies, function(b){ return Array.prototype.slice.call(b.rows); }))
    : Array.prototype.slice.call(table.rows);
  trs.forEach(function(tr){
    if (body.length >= MAX_ROWS) return;
    if (headRows.indexOf(tr) >= 0) return;
    if (tr.parentNode && tr.parentNode.tagName == 'THEAD') return;
    if (tr.style.display == 'none' || tr.hidden) return;
    if (tr.closest('table') != table) return;
    var vals = [], ci = 0;
    Array.prototype.forEach.call(tr.cells, function(td){
      var cs = Math.max(1, td.colSpan || 1);
      var t = cellText(td);
      vals[ci] = t;
      for (var k = 1; k < cs; k++) vals[ci + k] = '';
      ci += cs;
    });
    // group / empty-message rows (one wide cell) are not data rows
    if (tr.cells.length <= 1 && labels.length > 2) return;
    if (!vals.some(function(v){ return v; })) return;
    body.push(vals);
  });
  if (!labels.length && body.length){
    labels = body[0].map(function(v, i){ return { text: 'عمود ' + (i + 1), skip: false }; });
  }
  return { labels: labels.map(function(l){ return l.text; }), skip: labels.map(function(l){ return l.skip; }), rows: body };
}

function tableTitle(el, i){
  // the closest heading before the table inside its card, else the card title, else «جدول n»
  var card = el.closest('.card, .modal-content, .tab-pane') || el.parentNode;
  var best = '';
  if (card){
    var hs = card.querySelectorAll('.card-title, h1, h2, h3, h4, h5, h6, .modal-title');
    for (var k = 0; k < hs.length; k++){
      var h = hs[k];
      if (h.closest('table')) continue;
      if (!(h.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING)) continue;
      var t = clean(headingText(h), 60);
      if (t) best = t;
    }
  }
  if (!best && el.caption) best = clean(el.caption.textContent, 60);
  // no heading: the first table is the page's own list («استعراض الطلاب»)
  return maskPrivate(best) || (i == 0 && pageTitle()) || ('جدول ' + (i + 1));
}

function headingText(h){
  // the heading text without its buttons / inputs (page headers here carry their toolbar inline)
  var out = [];
  (function walk(n){
    for (var c = n.firstChild; c; c = c.nextSibling){
      if (c.nodeType == 3) out.push(c.nodeValue);
      else if (c.nodeType == 1 && !skipEl(c) && ['INPUT', 'SELECT', 'TEXTAREA', 'A', 'LABEL'].indexOf(c.tagName) < 0 && !c.classList.contains('form-check')) walk(c);
    }
  })(h);
  return out.join(' ');
}

/* ---------------------------------------------------------------- columns --------------------------------------- */

function typeOf(values){
  var vals = values.filter(function(v){ return v !== ''; });
  if (!vals.length) return 'text';
  var n = 0, d = 0, t = 0;
  var distinct = {};
  vals.forEach(function(v){
    var s = latinDigits(v).replace(/,/g, '').trim();
    if (/^-?\d+(\.\d+)?\s*%?$/.test(s)) n++;
    if (/^\d{4}[-/]\d{1,2}[-/]\d{1,2}/.test(s) || /^\d{1,2}[-/]\d{1,2}[-/]\d{4}/.test(s)) d++;
    if (/^\d{1,2}:\d{2}(:\d{2})?\s*(ص|م|am|pm)?$/i.test(s)) t++;
    distinct[v] = (distinct[v] || 0) + 1;
  });
  var L = vals.length, D = Object.keys(distinct).length;
  // «الفصل 1 / 2 / 3»: a few small numbers repeated are categories, not quantities
  if (n == L && L >= 4 && D <= 12 && L / D >= 3 && vals.every(function(v){ return /^\d{1,2}$/.test(latinDigits(v).trim()); })) return 'enum';
  if (n / L >= 0.9) return 'number';
  if (d / L >= 0.8) return 'date';
  if (t / L >= 0.8) return 'time';
  if (L >= 2 && D <= 25 && L / D >= 1.5 && vals.every(function(v){ return v.length <= 60; })) return 'enum';
  return 'text';
}

function looksPersonalValues(values){
  var sample = values.filter(function(v){ return v; }).slice(0, 60);
  if (!sample.length) return false;
  var hits = sample.filter(function(v){ var s = latinDigits(v); return PHONE_RE.test(s) || NATID_RE.test(s) || EMAIL_RE.test(s); }).length;
  return hits / sample.length >= 0.3;
}

// «اسم المادة / الفصل / المجموعة …» name a thing, not a person
var NOT_PERSONAL = /اسم\s*(ال)?(مادة|فصل|صف|مجموعة|حصة|قالب|خدمة|مدرسة|جهاز|ملف|دورة|لجنة|مقرر|مرحلة|قسم|نشاط|اختبار|تقرير|رسالة)/;

export function isPersonalLabel(label){
  label = String(label || '');
  return PERSONAL_LABEL.test(label) && !NOT_PERSONAL.test(label);
}

function describeColumns(labels, rows, skipFlags){
  var cols = [];
  labels.forEach(function(label, i){
    if (cols.length >= MAX_COLS) return;
    label = clean(label, 60);
    if (skipFlags && skipFlags[i]) return;
    var values = rows.map(function(r){ return r[i] == null ? '' : r[i]; });
    var filled = values.filter(function(v){ return v; }).length;
    if (SKIP_LABEL.test(label) && (label != 'م' || isSerial(values))) return;
    if (!label && !filled) return;
    if (!filled && rows.length > 0) return;
    var type = typeOf(values);
    var personal = isPersonalLabel(label) || PERSON_EXACT.test(label) || (PERSON_LABEL.test(label) && type == 'text') || looksPersonalValues(values);
    cols.push({ key: 'c' + i, index: i, label: label || ('عمود ' + (i + 1)), type: type, personal: personal });
  });
  return cols;
}

function isSerial(values){
  return values.slice(0, 20).every(function(v, i){ return !v || latinDigits(v).trim() == String(i + 1); });
}

// «1448/5/9», «2026-10-31», «31/10/2026» → «1448-05-09» (sortable); '' when not a date
function dateKey(t){
  var m = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/.exec(t) || /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/.exec(t);
  if (!m) return '';
  var y = m[1].length == 4 ? m[1] : m[3], d = m[1].length == 4 ? m[3] : m[1];
  return y + '-' + ('0' + m[2]).slice(-2) + '-' + ('0' + d).slice(-2) + t.slice(m[0].length);
}

function num(v){
  var s = latinDigits(v).replace(/[,%\s]/g, '');
  var x = parseFloat(s);
  return isNaN(x) ? null : x;
}

function statsFor(col, rows){
  if (col.personal) return null;
  var vals = rows.map(function(r){ return r[col.index] || ''; }).filter(function(v){ return v; });
  if (!vals.length) return null;
  if (col.type == 'enum'){
    var m = {};
    vals.forEach(function(v){ var k = maskPrivate(clean(v.replace(/\n/g, ' '), 40)); m[k] = (m[k] || 0) + 1; });
    var top = Object.keys(m).map(function(k){ return [k, m[k]]; }).sort(function(a, b){ return b[1] - a[1]; }).slice(0, 8);
    var empty = rows.length - vals.length;
    if (empty > 0) top.push(['(فارغ)', empty]);
    return { top: top };
  }
  if (col.type == 'number'){
    var ns = vals.map(num).filter(function(x){ return x != null; });
    if (!ns.length) return null;
    var sum = ns.reduce(function(a, b){ return a + b; }, 0);
    return { min: Math.min.apply(null, ns), max: Math.max.apply(null, ns), sum: Math.round(sum * 100) / 100, avg: Math.round((sum / ns.length) * 100) / 100 };
  }
  if (col.type == 'date' || col.type == 'time'){
    var keyed = vals.map(function(v){ var t = latinDigits(v).trim().slice(0, 19); return [dateKey(t) || t, t]; })
      .sort(function(a, b){ return a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0; });
    return { min: keyed[0][1], max: keyed[keyed.length - 1][1] };
  }
  return null;   // free text: never sampled (it can carry names)
}

/* ---------------------------------------------------------------- page parts ------------------------------------ */

function controlLabel(el){
  var id = el.id, t = '';
  if (id){
    try { var l = document.querySelector('label[for="' + id.replace(/"/g, '') + '"]'); if (l) t = l.textContent; } catch (e) { /* bad id */ }
  }
  if (!t){
    var lab = el.closest('label');
    if (lab) t = lab.textContent;
  }
  if (!t) t = el.getAttribute('aria-label') || '';
  if (!t){
    // the text just before the control (pages here use <span>/<p>/<h5> labels)
    var p = el.previousElementSibling, hops = 0;
    while (p && hops < 3 && !t){
      if (p.tagName != 'BR' && !/^(INPUT|SELECT|TEXTAREA|BUTTON)$/.test(p.tagName)){
        var s = clean(p.textContent, 50);
        if (s && s.length <= 45) t = s;
      }
      p = p.previousElementSibling; hops++;
    }
  }
  if (!t && el.tagName == 'SELECT' && el.options.length && isPrompt(el.options[0])) t = el.options[0].text.replace(/^[-\s]+|[-\s]+$/g, '');
  if (!t && el.parentNode){
    var own = Array.prototype.filter.call(el.parentNode.childNodes, function(n){ return n.nodeType == 3; })
      .map(function(n){ return n.nodeValue; }).join(' ');
    own = clean(own, 50);
    if (own && own.length <= 45) t = own;
  }
  if (!t && el.parentNode && el.parentNode.parentNode && el.parentNode.children.length == 1){
    var prev = el.parentNode.previousElementSibling;
    if (prev && !/^(INPUT|SELECT|TEXTAREA|BUTTON)$/.test(prev.tagName)){ var s2 = clean(prev.textContent, 50); if (s2 && s2.length <= 45) t = s2; }
  }
  if (!t) t = el.getAttribute('placeholder') || '';
  if (!t && el.tagName == 'SELECT' && el.options.length){
    var f = el.options[0];
    if (!f.value || f.value == 'all') t = f.text;
  }
  if (!t) t = el.getAttribute('name') || '';
  return clean(t.replace(/[:：*]+\s*$/, ''), 40);
}

// «-- اختر --», «استعراض فصل معين»: the empty first option of a select (not «الكل»)
function isPrompt(o){
  return !!o && (!o.value || o.value == 'all') && !/^(الكل|الجميع|all|كل .*)$/i.test(clean(o.text, 40));
}

function filtersOf(rootList){
  var out = {}, n = 0;
  rootList.forEach(function(root){
    root.querySelectorAll('select, input, .v-select, .flatpickr-input').forEach(function(el){
      if (n >= 12 || inSaud(el) || el.closest('table') || !isVisible(el)) return;
      if (el.closest('.modal') && !el.closest('.modal.show')) return;
      var label = '', value = '';
      if (el.classList && el.classList.contains('v-select')){
        label = controlLabel(el);
        value = clean(Array.prototype.map.call(el.querySelectorAll('.vs__selected'), function(s){ return s.textContent; }).join('، '), 60);
      } else if (el.tagName == 'SELECT'){
        var o = el.options[el.selectedIndex];
        if (!o) return;
        label = controlLabel(el);
        // a prompt still selected = no filter chosen
        value = clean(el.multiple ? Array.prototype.filter.call(el.options, function(x){ return x.selected; }).map(function(x){ return x.text; }).join('، ') : (isPrompt(o) ? 'الكل' : o.text), 60);
      } else if (el.tagName == 'INPUT'){
        var ty = (el.type || 'text').toLowerCase();
        if (['hidden', 'password', 'file', 'button', 'submit', 'reset', 'image', 'color', 'range'].indexOf(ty) >= 0) return;
        if (el.classList.contains('vs__search')) return;
        label = controlLabel(el);
        if (ty == 'checkbox' || ty == 'radio'){
          if (!el.checked || ty == 'checkbox' && !label) return;
          value = ty == 'checkbox' ? 'نعم' : clean(label, 40);
          if (ty == 'radio') label = clean(el.name || 'خيار', 30);
        } else {
          value = clean(el.value, 60);
          // what the school types to search can be a name / number: never sent
          if (ty == 'search' || ty == 'tel' || ty == 'email' || /(بحث|ابحث|search)/i.test(label + ' ' + (el.placeholder || ''))) return;
        }
      }
      if (!label || !value) return;
      if (isPersonalLabel(label) || PERSON_LABEL.test(label) && !/^(الكل|all)$/i.test(value) && !/صف|فصل|مرحلة|مجموعة/.test(label)) return;
      value = maskPrivate(value);
      if (out[label] == null){ out[label] = value; n++; }
    });
  });
  return out;
}

function cardsOf(rootList){
  var cards = [], seen = {};
  rootList.forEach(function(root){
    var els = root.querySelectorAll('h1, h2, h3, h4, h5, h6, strong, b, span, div, p');
    for (var i = 0; i < els.length && cards.length < 16; i++){
      var el = els[i];
      if (el.children.length) continue;
      var txt = latinDigits(clean(el.textContent, 30));
      if (!/^-?\d[\d,]*(\.\d+)?\s*%?$/.test(txt)) continue;
      if (el.closest('table, button, a, select, .dropdown-menu, .pagination, .main-menu, .flatpickr-calendar') || inSaud(el) || !isVisible(el)) continue;
      // the label: the smallest ancestor (≤ 3 levels) that has a short text besides the number
      var box = el.parentElement, label = '';
      for (var k = 0; k < 3 && box && !label; k++){
        if (box.querySelector('table, input, select, textarea')) break;
        var all = clean(box.textContent, 120);
        if (all.length > 90) break;
        var rest = clean(all.replace(el.textContent.trim(), ' '), 60);
        if (/[؀-ۿa-z]{2,}/i.test(rest) && !/\d{3,}/.test(latinDigits(rest))) label = rest.replace(/\n/g, ' ');
        box = box.parentElement;
      }
      if (!label || seen[label]) continue;
      seen[label] = 1;
      cards.push({ label: maskPrivate(clean(label, 40)), value: num(txt) != null && !/%/.test(txt) ? num(txt) : txt });
    }
  });
  return cards;
}

function textsOf(rootList){
  var out = [], seen = {};
  rootList.forEach(function(root){
    root.querySelectorAll('h1, h2, h3, h4, h5, .card-title, .alert, .modal-title').forEach(function(el){
      if (out.length >= 10 || inSaud(el) || el.closest('table') || !isVisible(el)) return;
      if (el.parentElement && el.parentElement.closest('.alert')) return;
      var t = clean(el.classList.contains('alert') ? el.textContent : headingText(el), 160).replace(/\n+/g, ' — ');
      if (!t || t.length < 3 || seen[t] || /^-?[\d٠-٩,.%\s]+$/.test(t)) return;
      seen[t] = 1;
      out.push(maskPrivate(t));
    });
  });
  return out;
}

export function pageTitle(){
  var t = '';
  var active = document.querySelector('.main-menu .nav-item.active .menu-title, .main-menu li.active > a .menu-title');
  if (active) t = clean(active.textContent, 60);
  if (!t){
    var h = document.querySelector('.content-body h1, .content-body h2, .content-body h3, .content-body h4, .content-body .card-title');
    if (h) t = clean(headingText(h), 60);
  }
  if (!t) t = clean((document.title || '').split(/[—|-]/)[0], 60);
  return maskPrivate(t.replace(/\n/g, ' '));
}

/* ---------------------------------------------------------------- build ----------------------------------------- */

// every table of the page with all its rows (stays in the browser)
export function extractTables(){
  var rootList = roots();
  var comps = componentTables();
  var compEls = comps.map(function(c){ return c.el; });
  var raw = [];
  comps.forEach(function(c){
    try {
      var d = c.kind == 'btable' ? fromBTable(c.vm) : fromGoodTable(c.vm);
      raw.push({ el: c.el, source: c.kind, labels: d.labels, rows: d.rows, allRows: d.allRows, skip: null });
    } catch (e) { /* a table we cannot read: the DOM path below may still find it */ }
  });
  rootList.forEach(function(root){
    root.querySelectorAll('table').forEach(function(t){
      if (inSaud(t) || !isVisible(t)) return;
      if (t.parentElement && t.parentElement.closest('table')) return;        // nested layout tables
      if (compEls.some(function(e){ return e.contains(t); })) return;
      if (t.closest('.flatpickr-calendar, .main-menu, .b-calendar')) return;
      var d = fromPlainTable(t);
      if (!d.labels.length || !d.rows.length && d.labels.length < 2) return;
      raw.push({ el: t, source: 'dom', labels: d.labels, rows: d.rows, allRows: d.rows, skip: d.skip });
    });
  });
  raw.sort(function(a, b){ return a.el === b.el ? 0 : (a.el.compareDocumentPosition(b.el) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1); });
  var tables = [];
  raw.forEach(function(r){
    if (tables.length >= MAX_TABLES) return;
    var cols = describeColumns(r.labels, r.allRows, r.skip);
    if (!cols.length) return;
    var i = tables.length;
    var t = {
      id: 't' + (i + 1),
      title: tableTitle(r.el, i),
      source: r.source,
      columns: cols,
      rows: r.rows,                 // shown on the page now
      allRows: r.allRows,           // everything the page holds (client-paged tables: more than shown)
      total: r.allRows.length,      // known number of rows behind the table (an adapter may know more)
      more: r.allRows.length > r.rows.length,
      adapter: null,
    };
    // a server-paged page: its adapter knows if more rows exist and can fetch them («كل البيانات»)
    var ad = r.source == 'dom' ? adapterFor(window.location.pathname, r.el) : null;
    if (ad){
      var info = {};
      try { info = ad.info(r.el) || {}; } catch (e) { info = {}; }
      t.adapter = ad.name;
      t.more = !!info.more;
      t.total = info.total != null ? Math.max(info.total, r.rows.length) : (t.more ? null : r.rows.length);
    }
    tables.push(t);
  });
  return tables;
}

function snapTable(t, opts){
  var stats = {};
  if (opts.stats !== false){
    t.columns.forEach(function(c, i){
      if (opts.maxStatCols && i >= opts.maxStatCols) return;
      var s = statsFor(c, t.allRows || t.rows);
      if (!s) return;
      if (s.top && opts.top) s.top = s.top.slice(0, opts.top);
      stats[c.key] = s;
    });
  }
  // row_count: every row behind the table when known (a server-paged list: its total), visible_count: shown now
  var out = {
    id: t.id, title: t.title, row_count: t.total != null ? t.total : t.rows.length, visible_count: t.rows.length,
    columns: t.columns.map(function(c){
      var o = { key: c.key, label: opts.shortLabels ? clean(c.label, 24) : maskPrivate(c.label), type: c.type };
      if (c.personal) o.personal = true;
      return o;
    }),
  };
  if (t.more) out.has_more = true;               // the page holds only part of the rows (paged)
  if (Object.keys(stats).length) out.stats = stats;
  return out;
}

// fits the snapshot into MAX_BYTES by dropping the least useful parts first
function fit(base, tables){
  var plans = [
    { texts: 10, cards: 16, filters: 12, tables: 6, top: 8 },
    { texts: 6, cards: 12, filters: 10, tables: 6, top: 5 },
    { texts: 4, cards: 10, filters: 8, tables: 4, top: 4, maxStatCols: 12 },
    { texts: 3, cards: 8, filters: 8, tables: 3, top: 3, maxStatCols: 8 },
    { texts: 2, cards: 6, filters: 6, tables: 2, top: 3, maxStatCols: 6, shortLabels: true },
    { texts: 0, cards: 4, filters: 4, tables: 2, stats: false, shortLabels: true },
    { texts: 0, cards: 0, filters: 3, tables: 1, stats: false, shortLabels: true },
  ];
  var snap, json;
  for (var i = 0; i < plans.length; i++){
    var p = plans[i];
    snap = {
      route: base.route, title: base.title,
      filters: pick(base.filters, p.filters),
      cards: base.cards.slice(0, p.cards),
      tables: tables.slice(0, p.tables).map(function(t){ return snapTable(t, p); }),
      texts: base.texts.slice(0, p.texts),
    };
    json = JSON.stringify(snap);
    if (byteLength(json) <= MAX_BYTES) return { snap: snap, json: json };
  }
  // last resort: fewer columns
  snap.tables.forEach(function(t){ t.columns = t.columns.slice(0, 12); });
  json = JSON.stringify(snap);
  return { snap: snap, json: json };
}

function pick(obj, n){
  var out = {};
  Object.keys(obj).slice(0, n).forEach(function(k){ out[k] = obj[k]; });
  return out;
}

export function buildSnapshot(){
  var rootList = roots();
  var tables = [];
  try { tables = extractTables(); } catch (e) { tables = []; }
  var base = {
    route: window.location.pathname,
    title: pageTitle(),
    filters: safe(function(){ return filtersOf(rootList); }, {}),
    cards: safe(function(){ return cardsOf(rootList); }, []),
    texts: safe(function(){ return textsOf(rootList); }, []),
  };
  var f = fit(base, tables);
  return { snapshot: f.snap, json: f.json, tables: tables };
}

function safe(fn, dflt){
  try { return fn(); } catch (e) { return dflt; }
}
